// The Sounds browser's query: search text, filters and sort, as the app's
// SoundQuery (lib/models/sound.dart) and the filter chips of
// sounds_browser.dart build it. Pure functions, so the route, the API and
// the chips agree, and tests can pin them.
//
//   a query  { q, kind, key, scale, bpm, tags, license, following, sort }
//     q          words to search (#tags are split off into `tags`)
//     kind       null | "loop" | "one_shot"
//     key        null | a root ("C", "F#", "Bb"), with scale "minor" for "F#m"
//     scale      null | "major" | "minor"
//     bpm        null | a BPM_RANGES id ("90-120")
//     tags       every one must match (instruments are tags too)
//     license    null | "free"
//     following  from people you follow
//     sort       "new" | "popular" | "bpm"
//
// In the route (SIGNED_IN_PLAN section 4): q, kind, key, bpm, tags,
// license, following, sort. "key" carries the scale: "F#m", "F#", "minor".
import { displayKey } from "./musicKey.js";

export const SOUND_SORTS = [
  { value: "new", label: "Newest" },
  { value: "popular", label: "Popular" },
  { value: "bpm", label: "BPM" },
];
// The app's SoundQuery default.
export const DEFAULT_SORT = "popular";

export const SOUND_KINDS = [
  { value: "all", label: "All" },
  { value: "loop", label: "Loops" },
  { value: "one_shot", label: "One-shots" },
];

// The Instrument chip: each is a tag (the catalog has no instrument field).
export const INSTRUMENTS = [
  { tag: "bass", label: "Bass" },
  { tag: "drums", label: "Drums" },
  { tag: "guitar", label: "Guitar" },
  { tag: "keys", label: "Keys" },
  { tag: "pad", label: "Pads" },
  { tag: "synth", label: "Synth" },
  { tag: "vocals", label: "Vocals" },
];
const INSTRUMENT_TAGS = new Set(INSTRUMENTS.map((i) => i.tag));

// The Key chip's roots: wire spelling, then what the menu says.
export const KEY_ROOTS = [
  ["C", "C"],
  ["C#", "C♯"],
  ["D", "D"],
  ["Eb", "E♭"],
  ["E", "E"],
  ["F", "F"],
  ["F#", "F♯"],
  ["G", "G"],
  ["Ab", "A♭"],
  ["A", "A"],
  ["Bb", "B♭"],
  ["B", "B"],
];
const ROOTS = new Set(KEY_ROOTS.map(([w]) => w));

export const BPM_RANGES = [
  { id: "under-90", label: "Under 90", min: null, max: 89 },
  { id: "90-120", label: "90–120", min: 90, max: 120 },
  { id: "120-140", label: "120–140", min: 120, max: 140 },
  { id: "over-140", label: "Over 140", min: 141, max: null },
];
const bpmRange = (id) => BPM_RANGES.find((r) => r.id === id) || null;

export function emptyQuery() {
  return { q: "", kind: null, key: null, scale: null, bpm: null, tags: [], license: null, following: false, sort: DEFAULT_SORT };
}

const first = (v) => (Array.isArray(v) ? v[0] : v);
const str = (v) => (typeof first(v) === "string" ? first(v).trim() : "");

/** "#ambient warm pad" → { words: "warm pad", tags: ["ambient"] }. */
export function splitTags(text) {
  const tags = [];
  const words = [];
  for (const w of String(text || "").split(/\s+/)) {
    if (w.length > 1 && w.startsWith("#")) {
      const t = w.slice(1).toLowerCase();
      if (!tags.includes(t)) tags.push(t);
    } else if (w) {
      words.push(w);
    }
  }
  return { words: words.join(" "), tags };
}

/** A query from the route's query object; unknown values are dropped. */
export function queryFromRoute(rq = {}) {
  const out = emptyQuery();
  out.q = str(rq.q);
  const kind = str(rq.kind).toLowerCase().replace("-", "_");
  out.kind = kind === "loop" || kind === "one_shot" ? kind : null;
  const key = str(rq.key);
  if (/^(major|minor)$/i.test(key)) {
    out.scale = key.toLowerCase();
  } else if (key) {
    const minor = /m$/.test(key);
    const root = minor ? key.slice(0, -1) : key;
    if (ROOTS.has(root)) {
      out.key = root;
      if (minor) out.scale = "minor";
    }
  }
  out.bpm = bpmRange(str(rq.bpm)) ? str(rq.bpm) : null;
  out.tags = str(rq.tags)
    .split(",")
    .map((t) => t.trim().replace(/^#/, "").toLowerCase())
    .filter((t, i, all) => t && all.indexOf(t) === i);
  out.license = str(rq.license) === "free" ? "free" : null;
  out.following = ["1", "true"].includes(str(rq.following));
  const sort = str(rq.sort);
  out.sort = SOUND_SORTS.some((s) => s.value === sort) ? sort : DEFAULT_SORT;
  return out;
}

/** The route's query for a query: only what differs from the defaults. */
export function routeFromQuery(query) {
  const q = { ...emptyQuery(), ...(query || {}) };
  const out = {};
  if (q.q && q.q.trim()) out.q = q.q.trim();
  if (q.kind) out.kind = q.kind;
  if (q.key) out.key = q.scale === "minor" ? `${q.key}m` : q.key;
  else if (q.scale) out.key = q.scale;
  if (q.bpm) out.bpm = q.bpm;
  if (q.tags && q.tags.length) out.tags = q.tags.join(",");
  if (q.license) out.license = q.license;
  if (q.following) out.following = "1";
  if (q.sort && q.sort !== DEFAULT_SORT) out.sort = q.sort;
  return out;
}

/** GET /sounds parameters (SoundQuery.toParams). */
export function toParams(query, { cursor = null, limit = 30 } = {}) {
  const q = { ...emptyQuery(), ...(query || {}) };
  const out = {};
  if (q.q && q.q.trim()) out.q = q.q.trim();
  if (q.kind) out.kind = q.kind;
  if (q.key) out.key = q.scale === "minor" && !q.key.endsWith("m") ? `${q.key}m` : q.key;
  else if (q.scale) out.scale = q.scale;
  const r = bpmRange(q.bpm);
  if (r && r.min != null) out.bpm_min = r.min;
  if (r && r.max != null) out.bpm_max = r.max;
  if (q.tags && q.tags.length) out.tags = q.tags.join(",");
  if (q.license) out.license = q.license;
  if (q.following) out.following = "1";
  out.sort = q.sort || DEFAULT_SORT;
  out.limit = Math.min(Math.max(Number(limit) || 30, 1), 50);
  if (cursor) out.cursor = cursor;
  return out;
}

/** Any filter beyond the search text and sort (SoundQuery.filtered). */
export function isFiltered(query) {
  const q = query || {};
  return !!(q.kind || q.key || q.scale || q.bpm || (q.tags && q.tags.length) || q.license || q.following);
}

/** Every filter off; the text and sort stay (clearFilters). */
export function clearFilters(query) {
  return { ...emptyQuery(), q: (query && query.q) || "", sort: (query && query.sort) || DEFAULT_SORT };
}

export function instrumentTags(query) {
  return ((query && query.tags) || []).filter((t) => INSTRUMENT_TAGS.has(t));
}

/** Tags the Instrument chip doesn't show (the "Tags: ambient" chip). */
export function otherTags(query, { desktop = true } = {}) {
  return ((query && query.tags) || []).filter((t) => !desktop || !INSTRUMENT_TAGS.has(t));
}

/** One instrument at a time: the others go, the rest of the tags stay. */
export function withInstrument(query, tag) {
  const rest = ((query && query.tags) || []).filter((t) => !INSTRUMENT_TAGS.has(t));
  return { ...query, tags: tag ? [...rest, tag] : rest };
}

export function instrumentLabel(query) {
  const on = INSTRUMENTS.filter((i) => instrumentTags(query).includes(i.tag)).map((i) => i.label);
  return on.length ? on.join(", ") : "Instrument";
}

/** "Key", "Key: F♯m", "Minor" or "Major". */
export function keyLabel(query) {
  const q = query || {};
  if (q.key) return `Key: ${displayKey(q.key)}${q.scale === "minor" ? "m" : ""}`;
  if (q.scale) return q.scale === "minor" ? "Minor" : "Major";
  return "Key";
}

/** "BPM", "BPM 90–120", "BPM under 90" or "BPM over 140". */
export function bpmLabel(query) {
  const r = bpmRange(query && query.bpm);
  if (!r) return "BPM";
  if (r.min == null) return `BPM under ${r.max + 1}`;
  if (r.max == null) return `BPM over ${r.min - 1}`;
  return `BPM ${r.min}–${r.max}`;
}

export function sortLabel(sort) {
  return (SOUND_SORTS.find((s) => s.value === sort) || SOUND_SORTS.find((s) => s.value === DEFAULT_SORT)).label;
}

/** "1,284 sounds"; blank until the server sends a total (SI-13). */
export function countLabel(total, one = "sound", many = "sounds") {
  if (total == null || !Number.isFinite(Number(total))) return "";
  const n = Number(total);
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}
